import type { CSSProperties, FC, ReactNode } from 'react'
import type { CyclingWordProps } from '@/types'

import { useEffect, useState } from 'react'

import { EASE } from '@/constants/type'

/**
 * Fade timings. The incoming word waits for the outgoing one to clear before it
 * starts, because two different words cross-fading in the same cell overlap into
 * illegible type. Sequencing them keeps only one word on screen at a time.
 */
const FADE_OUT = 0.38
const FADE_IN = 0.55
const FADE_IN_DELAY = 0.42

/** A little travel on the way in and out, so the swap reads as motion. */
const FADE_SHIFT = '0.16em'

/**
 * A slot that swaps one word for the next: the outgoing word lifts away, the
 * incoming one rises into its place, both clipped by the same mask.
 *
 * `inline-grid` stacks every word in a single cell, so the slot is as wide as
 * its longest word and nothing is measured at runtime.
 *
 * Cycling stops entirely under `prefers-reduced-motion`, which leaves the first
 * word standing — so it has to read correctly on its own.
 */
export const CyclingWord: FC<CyclingWordProps> = ({
    words,
    interval = 2400,
    variant = 'rise',
    className,
    style
}): ReactNode => {
    const [index, setIndex] = useState(0)
    const [reduced, setReduced] = useState(false)

    useEffect(() => {
        setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    }, [])

    useEffect(() => {
        if (reduced || words.length < 2) return

        const id = window.setInterval(() => {
            setIndex(current => (current + 1) % words.length)
        }, interval)

        return () => {
            window.clearInterval(id)
        }
    }, [reduced, interval, words.length])

    const leaving = (index - 1 + words.length) % words.length
    const isFade = variant === 'fade'

    return (
        <span
            className={className}
            style={{
                display: 'inline-grid',
                /* The fade only drifts a fraction of a line and is transparent at
                   both ends, so it needs no mask — and skipping one keeps
                   descenders from being clipped. */
                overflow: isFade ? 'visible' : 'hidden',
                verticalAlign: 'bottom',
                ...(isFade
                    ? {}
                    : {
                          paddingTop: '0.16em',
                          paddingBottom: '0.2em',
                          marginTop: '-0.16em',
                          marginBottom: '-0.2em'
                      }),
                ...style
            }}
        >
            {words.map((word, position) => {
                const isCurrent = position === index
                const isLeaving = position === leaving && !isCurrent

                const fadeStyle: CSSProperties = isCurrent
                    ? {
                          opacity: 1,
                          transform: 'translateY(0)',
                          transition: reduced
                              ? 'none'
                              : `opacity ${FADE_IN}s ${EASE} ${FADE_IN_DELAY}s, transform ${FADE_IN}s ${EASE} ${FADE_IN_DELAY}s`
                      }
                    : isLeaving
                      ? {
                            opacity: 0,
                            transform: `translateY(-${FADE_SHIFT})`,
                            transition: reduced
                                ? 'none'
                                : `opacity ${FADE_OUT}s ${EASE}, transform ${FADE_OUT}s ${EASE}`
                        }
                      : {
                            /* Parked below, ready for its turn. No transition, so it
                               resets instantly and never animates across the box. */
                            opacity: 0,
                            transform: `translateY(${FADE_SHIFT})`,
                            transition: 'none'
                        }

                const wordStyle: CSSProperties = isFade
                    ? {
                          gridArea: '1 / 1',
                          justifySelf: 'start',
                          ...fadeStyle
                      }
                    : {
                          gridArea: '1 / 1',
                          justifySelf: 'start',
                          opacity: isCurrent ? 1 : 0,
                          filter: isCurrent ? 'blur(0px)' : 'blur(7px)',
                          transform: isCurrent
                              ? 'translateY(0)'
                              : isLeaving
                                ? 'translateY(-115%)'
                                : 'translateY(115%)',
                          /* Only the two words on stage animate; the rest reset
                             instantly to the waiting position underneath. */
                          transition:
                              reduced || (!isCurrent && !isLeaving)
                                  ? 'none'
                                  : `opacity 0.75s ${EASE}, transform 0.75s ${EASE}, filter 0.75s ${EASE}`
                      }

                return (
                    <span key={word} style={wordStyle}>
                        {word}
                    </span>
                )
            })}
        </span>
    )
}
